import React, { useContext, useMemo } from 'react'
import type { OptionsDocument } from 'paintscout'
import {
  DevPermissionsContext,
  DialogStackPortal,
  useClientOptions,
  useDialogs,
  useUser
} from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useQuoteMutations } from 'shared/hooks'
import { getQuoteOptions } from '@paintscout/util/builder/quote'
import PainterScheduleView, {
  AddAdditionalWorkPanel,
  PainterWorkOrderPanel,
  SlideOutPanel,
  ViewMediaPanel
} from '../PainterScheduleView'
import { getPainterSchedulePermissions } from './painterSchedulePageUtils'
import { usePainterAdditionalWorkHandlers } from './usePainterAdditionalWorkHandlers'
import { usePainterScheduleData } from './usePainterScheduleData'
import { usePainterScheduleHandlers } from './usePainterScheduleHandlers'

export default function PainterSchedulePage() {
  const { user, preferences, isPainter } = useUser()
  const { options } = useClientOptions()
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { saveQuote } = useQuoteMutations()
  const { painterAccount } = useFlags()
  const isPainterDevMock = useIsPainterDevMock()

  const permissions = useMemo(
    () =>
      getPainterSchedulePermissions({
        appMetadata: user?.app_metadata,
        isPainter,
        forcePainterPreview: painterAccount && !isPainter
      }),
    [isPainter, painterAccount, user?.app_metadata]
  )

  const { allowPaymentsOnInvoices } = useMemo(
    () => (options ? getQuoteOptions({ options: options as OptionsDocument }) : { allowPaymentsOnInvoices: true }),
    [options]
  )
  const userAvatarUrl = user?.user_metadata?.customAvatar?.value

  const { eventsState, setEventsState, setLocalQuoteOverride, loading } = usePainterScheduleData({
    userId: user?.user_id,
    currentUserId: user?.user_id,
    options: options as OptionsDocument,
    isPainter,
    showMockEvents: isPainterDevMock || (painterAccount && !isPainter)
  })

  const {
    notificationPrefs,
    handleNotificationPrefsChange,
    mediaPanel,
    setMediaPanel,
    showAdditionalWorkPanel,
    setShowAdditionalWorkPanel,
    addWorkEventId,
    setAddWorkEventId,
    workOrderPanel,
    setWorkOrderPanel,
    persistPainterQuote,
    handleAddPayment,
    handleOpenPayment,
    handleDirections,
    handleCall,
    handleMessage,
    handleAddTask,
    handleAddNote,
    handleAddFile
  } = usePainterScheduleHandlers({
    userId: user?.user_id,
    preferences,
    eventsState,
    setEventsState,
    options: options as OptionsDocument,
    saveQuote,
    setLocalQuoteOverride,
    openDialog,
    dismissDialog,
    dismissAllDialogs
  })

  const {
    handleViewMedia,
    handleAcceptSign,
    handleEditAdditionalWork,
    handleDeleteAdditionalWork,
    handleOpenAdditionalWorkCreateDialog,
    handleAddSelected,
    activeAdditionalWorkEvent,
    activeAdditionalWorkOptions
  } = usePainterAdditionalWorkHandlers({
    eventsState,
    setEventsState,
    options,
    addWorkEventId,
    setAddWorkEventId,
    setShowAdditionalWorkPanel,
    setMediaPanel,
    persistPainterQuote,
    openDialog,
    dismissDialog
  })

  return (
    <>
      <DialogStackPortal />
      <PainterScheduleView
        events={eventsState}
        loading={loading}
        userAvatarUrl={userAvatarUrl}
        permissions={permissions}
        showPayments={allowPaymentsOnInvoices ? undefined : false}
        notificationPrefs={notificationPrefs}
        onNotificationPrefsChange={handleNotificationPrefsChange}
        onDirections={handleDirections}
        onCall={handleCall}
        onMessage={handleMessage}
        onAddTask={handleAddTask}
        onAddNote={handleAddNote}
        onAddFile={handleAddFile}
        onViewMedia={handleViewMedia}
        onAddAdditionalWork={(eventId) => {
          setAddWorkEventId(eventId)
          setShowAdditionalWorkPanel(true)
        }}
        onAddPayment={handleAddPayment}
        onOpenPayment={handleOpenPayment}
        onAcceptSign={handleAcceptSign}
        onEditAdditionalWork={handleEditAdditionalWork}
        onDeleteAdditionalWork={handleDeleteAdditionalWork}
      />
      {workOrderPanel && (
        <SlideOutPanel width={960}>
          <PainterWorkOrderPanel
            title={workOrderPanel.title}
            event={workOrderPanel.event}
            permissions={permissions}
            onAddAdditionalWork={(eventId) => {
              setWorkOrderPanel(null)
              setAddWorkEventId(eventId)
              setShowAdditionalWorkPanel(true)
            }}
            onAcceptSign={handleAcceptSign}
            onEditAdditionalWork={handleEditAdditionalWork}
            onDeleteAdditionalWork={handleDeleteAdditionalWork}
            onViewMedia={handleViewMedia}
            onClose={() => setWorkOrderPanel(null)}
          />
        </SlideOutPanel>
      )}
      {mediaPanel && (
        <SlideOutPanel fullScreen>
          <ViewMediaPanel title={mediaPanel.title} items={mediaPanel.items} onClose={() => setMediaPanel(null)} />
        </SlideOutPanel>
      )}
      {showAdditionalWorkPanel && (
        <SlideOutPanel>
          <AddAdditionalWorkPanel
            options={activeAdditionalWorkOptions}
            onClose={() => {
              setShowAdditionalWorkPanel(false)
              setAddWorkEventId(null)
            }}
            onAddSelected={handleAddSelected}
            onAddLineItem={() => {
              if (activeAdditionalWorkEvent)
                handleOpenAdditionalWorkCreateDialog(activeAdditionalWorkEvent.id, 'lineItem')
            }}
          />
        </SlideOutPanel>
      )}
    </>
  )
}

function useIsPainterDevMock() {
  const { state } = useContext(DevPermissionsContext)
  return state.active && state.role === 'painter'
}
